import { TGetAllAvailablePrizesResponse } from '@daymade/api-contracts';
import AboutUsModule, {
  AboutUsModuleProps,
} from 'components/about-us/AboutUsModule';
import AboutUsSiteContainer from 'components/about-us/AboutUsSiteContainer';
import AspectRatioContainer from 'components/about-us/AspectRatioContainer';
import ScrollbarWidthCssVariable from 'components/common/ScrollBarCssVariable';
import { prizeTableByPlanType } from 'components/how-it-works/prizeTableData';
import ImageWithFallback from 'components/ImageWithFallback';
import MainLayout from 'components/layout/MainLayout';
import PageTitle from 'components/PageTitle';
import PlainLoadingSpinner from 'components/PlainLoadingSpinner';
import RoundedButton from 'components/RoundedButton';
import SubmitPrizeSection2026 from 'components/SubmitPrizeSection2026';
import { MainDrawScheduleCode, MainDrawTypeCode } from 'constants/Draw';
import { getPrizeTierByKey, PRIZE_TIERS } from 'constants/LottoPrizeValues';
import { MAIN_PRIZE_CATEGORIES_2026 } from 'constants/PrizeCategories';
import { useAllPrizesPageStore } from 'hooks/stores/useAllPrizesPageStore';
import { useScrollRestoration } from 'hooks/useScrollRestoration';
import { observer } from 'mobx-react-lite';
import { NextSeo } from 'next-seo';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { ReactElement, useEffect, useState } from 'react';
import {
  EecProduct,
  getProductListTrackingName,
  trackEecImpression,
  trackPrizeSearchRequest,
} from 'utils/analytics/analytics';
import { tryApi } from 'utils/api/common';
import { prizeApiClient } from 'utils/apiClient';
import { ASSET_PREFIX } from 'utils/constants';
import HttpStatusCode from 'utils/HttpStatusCode';
import PrizeCard2026 from '../PrizeCard2026';
import { prizePageIndexMetadata } from './AllPrizesPageShared';
import styles from './FilteredPrizeList.module.css';
import PrizeGroupingHeader from './PrizeGroupingHeader';
import YouMightAlsoLikePrizeGrouping from './YouMightAlsoLikePrizeGrouping';

interface FilteredPrizeListProps {
  category?: string | undefined;
  tier?: string | undefined;
  term?: string | undefined;
  /** The component to show in the header of the filter prize list. This component will be placed within a site module
   * and container
   */
  headerComponent: ReactElement;
  headerComponentColor?: AboutUsModuleProps['background'];
  showPrizeTierBadges: boolean;
  onPrizeCountChange?: (newPrizeCount: number) => void;
  onLoadingChange?: (newLoadingStatus: boolean) => void;
}

const storeSearchQuery = (searchTerm: string) => {
  const storeName = 'prizeSearch';
  const store = localStorage.getItem(storeName);

  let parsedStore: string[] | undefined;
  try {
    parsedStore = store && JSON.parse(store);
  } catch (e) {
    // If we couldn't parse the stored search queries, then we just don't have to care
  }
  if (!Array.isArray(parsedStore)) {
    parsedStore = [];
  }

  const exists = parsedStore.findIndex((item) => item === searchTerm);

  if (exists > -1) {
    parsedStore.splice(exists, 1);
  }

  if (parsedStore.length === 3) {
    parsedStore.splice(-1);
  }

  localStorage.setItem(
    storeName,
    JSON.stringify([searchTerm].concat(parsedStore)),
  );
};

const FilteredPrizeList = observer((props: FilteredPrizeListProps) => {
  const allPrizesPageStore = useAllPrizesPageStore();
  const [loading, setLoading] = useState(false);
  const initialCriteria = {
    term: props.term,
    category: props.category,
    tier: props.tier,
  };
  const [matchingPrizes, setMatchingPrizes] =
    useState<TGetAllAvailablePrizesResponse>(() =>
      allPrizesPageStore.getMatchingPrizes(initialCriteria),
    );
  const [isFallbackPrizes, setIsFallbackPrizes] = useState(
    allPrizesPageStore.getIsFallbackPrizes(initialCriteria),
  );
  const [allowLoadRequest, setAllowLoadRequest] = useState(
    () => allPrizesPageStore.getMatchingPrizes(initialCriteria).length === 0,
  );
  const [nextCursor, setNextCursor] = useState<string | undefined>(() =>
    allPrizesPageStore.getCursor(initialCriteria),
  );
  const router = useRouter();
  useScrollRestoration(router, true);

  const hasMore = nextCursor != null;

  useEffect(() => {
    trackEecImpression(
      getProductListTrackingName(props.category),
      EecProduct.LOTTERY,
    );
  }, [props.category]);

  const loadFilteredPrizes = async (
    tier?: string | undefined,
    category?: string | undefined,
    term?: string | undefined,
    cursor?: string | undefined,
  ) => {
    if (!cursor) {
      setMatchingPrizes([]);
    }
    setLoading(true);
    props.onLoadingChange?.(true);

    if (!cursor && term) {
      // Track the search request for the first page of a search term. This is so that we don't skew the
      // number of searches for terms that have lots of requests. We could include
      // offset in the future if we find that valuable
      trackPrizeSearchRequest(term);
    }

    const result = await tryApi(
      prizeApiClient.public.getPrizesSearch({
        query: {
          drawTypeCode: MainDrawTypeCode,
          drawScheduleCode: MainDrawScheduleCode.TUESDAY,
          tiers: tier ? [tier] : undefined,
          categories: category ? [category] : undefined,
          term: term,
          // This limit is a multiple of 2 and 3, since our views are 2-column or 3-column
          limit: 18,
          cursor: cursor,
        },
      }),
    );

    setLoading(false);
    props.onLoadingChange?.(false);
    if (result.status === HttpStatusCode.OK) {
      const resultsToShow = result.body.fallbackResults || result.body.results;
      const isFallbackResults = result.body.fallbackResults != undefined;
      const criteria = {
        tier,
        category,
        term,
      };

      setIsFallbackPrizes(isFallbackResults);

      if (!isFallbackResults && cursor) {
        setMatchingPrizes([...matchingPrizes, ...result.body.results]);
        allPrizesPageStore.setMatchingPrizes(
          criteria,
          [...matchingPrizes, ...result.body.results],
          result.body.resultCount,
          result.body.nextCursor,
          isFallbackResults,
        );
      } else {
        setMatchingPrizes(resultsToShow);
        allPrizesPageStore.setMatchingPrizes(
          criteria,
          resultsToShow,
          result.body.resultCount,
          result.body.nextCursor,
          isFallbackResults,
        );
      }
      setNextCursor(result.body.nextCursor);
      if (term && result.body.resultCount && !cursor) {
        storeSearchQuery(term);
      }
      props.onPrizeCountChange?.(result.body.resultCount);
    } else {
      throw result.error;
    }
  };
  useEffect(() => {
    if (allowLoadRequest) {
      loadFilteredPrizes(
        props.tier,
        props.category,
        props.term,
        // Clear the cursor whenever the search filters change, so that we start from the first "page"
        undefined,
      );
    } else {
      setAllowLoadRequest(true);
    }
  }, [props.category, props.tier, props.term]);

  const loadNext = () => {
    if (nextCursor) {
      loadFilteredPrizes(props.tier, props.category, props.term, nextCursor);
    } else {
      console.warn('Attempted to load the next cursor, which was nullish');
    }
  };

  return (
    <MainLayout additionalClassName="prizesPage" limitItemWidths>
      <NextSeo
        title={prizePageIndexMetadata.metaTitle}
        description={prizePageIndexMetadata.metaDescription}
        openGraph={{
          title: prizePageIndexMetadata.metaTitle,
          description: prizePageIndexMetadata.metaDescription,
        }}
      />
      <ScrollbarWidthCssVariable />
      <div className="daymadeTypographyReset">
        <PageTitle
          rebrandDm2025
          heroBackgroundImage={`${ASSET_PREFIX}/images/prizes/hero-background.svg`}
        >
          Prizes
        </PageTitle>
        {props.headerComponent && (
          <AboutUsModule background={props.headerComponentColor || 'grey'}>
            <AboutUsSiteContainer>{props.headerComponent}</AboutUsSiteContainer>
          </AboutUsModule>
        )}
        <AboutUsModule background="white" withReducedPadding>
          <AboutUsSiteContainer>
            {loading && matchingPrizes.length === 0 && (
              <PlainLoadingSpinner style={{ alignSelf: 'center' }} />
            )}
            {!loading && isFallbackPrizes && (
              <div
                className={
                  styles.noResultsContainer + ' aboutUsTypographyCentered'
                }
              >
                <p>
                  Oops, no results found.
                  <br />
                  Head back to our <Link href="/prizes">prize hub</Link> or
                  check out some popular prizes below!
                </p>
              </div>
            )}
            {matchingPrizes.length > 0 && (
              <>
                <div className={styles.filteredPrizeList}>
                  {matchingPrizes.map((x, i) => (
                    <div key={i} className={styles.filteredPrizeListItem}>
                      <PrizeCard2026
                        prize={x}
                        prizeTitleElementTagName={'h3'}
                        imageAspectRatio="SQUARE"
                        showPrizeTierBadge={props.showPrizeTierBadges}
                      />
                    </div>
                  ))}
                </div>
                <div className={styles.loadMoreContainer}>
                  {hasMore &&
                    (loading ? (
                      <PlainLoadingSpinner />
                    ) : (
                      <RoundedButton
                        onClick={loadNext}
                        rebrandDm2025
                        colorStyle={'OutlineDark'}
                      >
                        See more
                      </RoundedButton>
                    ))}
                </div>
              </>
            )}
          </AboutUsSiteContainer>
        </AboutUsModule>
        {renderYouMightAlsoLikePrizeGrouping(props)}
      </div>
    </MainLayout>
  );
});

export function renderGroupingHeader(
  categoryName: string | undefined,
  tierName: string | undefined,
  tierForMatches: 'PREMIUM' | 'LITE' | 'FREEMIUM',
  renderAsLink: boolean,
) {
  if (categoryName) {
    const category = MAIN_PRIZE_CATEGORIES_2026[categoryName];
    if (category) {
      return (
        <PrizeGroupingHeader
          categoryTitle={category.name}
          categorySubtitle={category.description}
          image={category.svg}
          groupingPageHref={'/prizes/' + category.categoryCode}
          renderAsLink={renderAsLink}
        />
      );
    }
  }

  if (tierName) {
    const tier = getPrizeTierByKey(tierName || '');
    if (!tier) {
      return null;
    }
    const criteria = prizeTableByPlanType[tierForMatches]?.prizes.find(
      (x) => tier.key == x.prizeTier.key,
    );
    return (
      <PrizeGroupingHeader
        categoryTitle={
          <>
            {tier.title}
            {tier.subtitle && <em>{tier.subtitle}</em>}
          </>
        }
        categorySubtitle={'Prizes ' + tier.examplePrizes}
        image={<img src={tier.badge} alt="" />}
        groupingPageHref={'/prizes/?tier=' + tier.key}
        criteria={criteria}
        renderAsLink={renderAsLink}
      />
    );
  }
}

export function renderYouMightAlsoLikePrizeGrouping(
  props: FilteredPrizeListProps,
) {
  let groupings: ReactElement[] | undefined;
  if (props.category) {
    groupings = Object.values(MAIN_PRIZE_CATEGORIES_2026)
      .filter((x) => x.categoryCode != props.category)
      .map((category) => (
        <Link href={category.href} className={styles.alsoLikeCard}>

          <AspectRatioContainer
            width={176}
            height={184}
            innerClassName={styles.imageContainer}
          >
            <ImageWithFallback
              src={category.examplePrizeImage}
              alt=""
              layout="fill"
              objectFit="cover"
            />
            <div className={styles.alsoLikeCategoryInfo}>
              {category.svg}
              <span>
                {category.name}
                <br />
                prizes
              </span>
            </div>
          </AspectRatioContainer>

        </Link>
      ));
  } else if (props.tier) {
    groupings = Object.values(PRIZE_TIERS)
      .filter((x) => x.key != props.tier)
      .map((tier) => (
        <Link href={'/prizes/?tier=' + tier.key} className={styles.alsoLikeCard}>

          <AspectRatioContainer
            width={182}
            height={91}
            innerClassName={styles.imageContainer}
          >
            <ImageWithFallback
              src={`https://daymade-website-assets.s3.eu-west-1.amazonaws.com/2025/how-it-works/desktop/${tier.key}.jpg`}
              alt=""
              layout="fill"
              objectFit="cover"
            />
            <div className={styles.alsoLikeTierInfo}>
              <img src={tier.badge} alt="" />
              <span>
                {tier.title}
                {tier.subtitle && <em>{tier.subtitle}</em>}
                <br />
                prizes
              </span>
            </div>
          </AspectRatioContainer>
          <div className={styles.alsoLikeTextContainer}>
            worth up to
            <strong>£{tier.value.toLocaleString('en-GB')}</strong>
          </div>

        </Link>
      ));
  }

  if (!groupings) {
    return <SubmitPrizeSection2026 />;
  }

  return <YouMightAlsoLikePrizeGrouping groupings={groupings} />;
}

export default FilteredPrizeList;
